import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import { AuthContext } from "../../utils/context/auth";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../../utils/axios";
import OrderHistory from "./OrderHistory";

const RESTRICTED_EMAIL = "ijalab1808@gmail.com";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Syne:wght@300;400;500;600&display=swap');

.of-root *{box-sizing:border-box;margin:0;padding:0}
.of-root{
  --ink:#1E2A3A;
  --green:#4E8A3C;
  --green-light:#7BBF6A;
  --green-pale:#EBF5E7;
  --aqua:#7BBCC8;
  --aqua-pale:#E8F4F7;
  --gold:#C9A96E;
  --cream:#F7F6F2;
  --muted:#6B7A8D;
  --border:rgba(30,42,58,0.1);
  --white:#FFFFFF;
  font-family:'Syne',sans-serif;
  color:var(--ink);
  background:var(--cream);
  min-height:100vh;
  width:100%;
  overflow-x:hidden;
  padding:2.5rem max(1.5rem, calc((100vw - 1100px)/2 + 1.5rem));
}
.of-serif{font-family:'Playfair Display',Georgia,serif}

.of-header{margin-bottom:2rem}
.of-eyebrow{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:var(--green);margin-bottom:0.5rem;font-weight:500}
.of-title{font-family:'Playfair Display',serif;font-size:32px;color:var(--ink);font-weight:400;line-height:1.2}
.of-subtitle{font-size:13px;color:var(--muted);margin-top:0.5rem;font-weight:300;line-height:1.6}

/* TABS */
.of-tabs{display:flex;gap:0;margin-bottom:2rem;border-bottom:2px solid rgba(30,42,58,0.1)}
.of-tab{
  padding:0.75rem 1.75rem;font-size:12px;font-weight:500;
  font-family:'Syne',sans-serif;letter-spacing:0.5px;
  border:none;background:none;cursor:pointer;color:var(--muted);
  border-bottom:2px solid transparent;margin-bottom:-2px;
  transition:all 0.2s;
}
.of-tab:hover{color:var(--ink)}
.of-tab.active{color:var(--green);border-bottom-color:var(--green)}

.of-progress{display:flex;align-items:center;gap:0;margin-bottom:2.5rem}
.of-step-wrap{display:flex;align-items:center;gap:0;flex:1}
.of-step-wrap:last-child{flex:none}
.of-step-btn{display:flex;align-items:center;gap:0.6rem;padding:0.6rem 0;cursor:default;border:none;background:none;font-family:'Syne',sans-serif}
.of-step-dot{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;flex-shrink:0;transition:all 0.2s}
.of-step-dot.active{background:var(--green);color:white}
.of-step-dot.done{background:var(--green-pale);color:var(--green);border:1.5px solid var(--green)}
.of-step-dot.inactive{background:white;color:var(--muted);border:1.5px solid var(--border)}
.of-step-label{font-size:11px;font-weight:500;white-space:nowrap}
.of-step-label.active{color:var(--green)}
.of-step-label.done{color:var(--green)}
.of-step-label.inactive{color:var(--muted)}
.of-step-line{flex:1;height:1px;background:var(--border);margin:0 0.75rem}
.of-step-line.done{background:var(--green)}

.of-card{background:white;border:1px solid rgba(78,138,60,0.12);border-top:3px solid var(--green);border-radius:10px;overflow:hidden;margin-bottom:1.5rem;box-shadow:0 2px 12px rgba(30,42,58,0.05)}
.of-card-header{padding:1.5rem 2rem;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:0.75rem}
.of-card-icon{width:36px;height:36px;border-radius:8px;background:var(--green-pale);display:flex;align-items:center;justify-content:center;font-size:17px;flex-shrink:0}
.of-card-title{font-family:'Playfair Display',serif;font-size:18px;color:var(--ink);font-weight:400}
.of-card-body{padding:2rem}

.of-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem}
.of-full{grid-column:1/-1}
.of-field{display:flex;flex-direction:column;gap:0.4rem}
.of-label{font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:var(--muted);font-weight:500}
.of-required{color:var(--green);margin-left:2px}
.of-input,.of-select,.of-textarea{
  width:100%;background:var(--cream);
  border:1px solid var(--border);border-radius:6px;
  padding:0.75rem 1rem;font-size:13px;
  font-family:'Syne',sans-serif;color:var(--ink);
  outline:none;transition:border-color 0.2s,background 0.2s;
  -webkit-appearance:none;
}
.of-input::placeholder,.of-textarea::placeholder{color:rgba(30,42,58,0.3)}
.of-input:focus,.of-select:focus,.of-textarea:focus{border-color:var(--green);background:white}
.of-select option{background:white;color:var(--ink)}
.of-textarea{resize:vertical;min-height:80px;line-height:1.6}

.of-nav{display:flex;align-items:center;justify-content:space-between;margin-top:2rem;padding-top:1.5rem;border-top:1px solid var(--border)}
.of-btn-back{background:transparent;color:var(--muted);font-family:'Syne',sans-serif;font-size:12px;font-weight:500;padding:12px 24px;border-radius:6px;border:1px solid var(--border);cursor:pointer;letter-spacing:0.5px;transition:all 0.2s}
.of-btn-back:hover{border-color:var(--ink);color:var(--ink)}
.of-btn-next{background:var(--green);color:white;font-family:'Syne',sans-serif;font-size:12px;font-weight:500;padding:12px 28px;border-radius:6px;border:none;cursor:pointer;letter-spacing:0.5px;transition:background 0.2s}
.of-btn-next:hover{background:#3D7030}
.of-btn-next:disabled{opacity:0.5;cursor:not-allowed}
.of-btn-submit{background:var(--ink);color:white;font-family:'Syne',sans-serif;font-size:12px;font-weight:500;padding:12px 32px;border-radius:6px;border:none;cursor:pointer;letter-spacing:0.75px;text-transform:uppercase;transition:background 0.2s}
.of-btn-submit:hover{background:var(--green)}
.of-btn-submit:disabled{opacity:0.5;cursor:not-allowed}

@media (max-width: 768px) {
  .of-root{padding:2rem 1.25rem}
  .of-grid{grid-template-columns:1fr}
  .of-full{grid-column:1}
  .of-card-body{padding:1.5rem 1.25rem}
  .of-card-header{padding:1.25rem}
  .of-step-label{display:none}
}
`;

const STATUS_OPTIONS = [
  "Received", "Contacted - Waiting Patient Signature", "Ready to Bill",
  "Ready to Ship", "Shipped", "DENIED-READ DETAILS", "Completed",
  "Re-Ship Item", "Need Additional Information", "Medical Records Requested",
  "VOYAGE COMPLETED", "Collect CashPay", "Collect Deposit",
  "Under Review - On Hold", "Check Insurance", "Call Patient",
  "Checking Eligibility", "LVM", "Collect Copay/Coinsurance",
];

const MEDICAL_RECORDS_OPTIONS = [
  "Medical Records Completed",
  "Medical Records Needed",
  "Medical Records Requested",
];

const SIZE_OPTIONS = [
  "I", "I OT", "II", "II OT", "III", "III OT",
  "IV", "IV OT", "V", "V OT",
  "MOTIF S", "MOTIF M", "MOTIF L", "MOTIF XL", "Wrap-S",
];

const STEPS = [
  { label: "Patient & Provider", icon: "👤" },
  { label: "Insurance & Clinical", icon: "🏥" },
  { label: "Order Details", icon: "📋" },
];

const emptyForm = {
  patient_name_or_id: "",
  patient_dob: "",
  phone_number: "",
  email_address: "",
  physician: "",
  doctor_location: "",
  primary_insurance: "",
  primary_member_id: "",
  ins2: "",
  ins2_id: "",
  icd10_code: "",
  dx_code_2: "",
  ankle_measurement: "",
  calf_measurement: "",
  thigh_measurement: "",
  length_wraps: "",
  compression_sock_status: "",
  medical_records_status: "",
  date_order_received: "",
  date_sent_to_billing: "",
  hcpc: "",
  product_sku: "",
  size: "",
};

const Field = ({ label, required, children }) => (
  <div className="of-field">
    <label className="of-label">{label}{required && <span className="of-required">*</span>}</label>
    {children}
  </div>
);

const OrderForm = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("new");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  if (user?.user?.email !== RESTRICTED_EMAIL && user?.email !== RESTRICTED_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const axiosInstance = axios();
      await axiosInstance.post("/orders/compressed-sock/create/", form);
      toast.success("Order submitted successfully!");
      setForm(emptyForm);
      setStep(0);
      // Switch to history tab so user can see their new order
      setActiveTab("history");
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="of-root">

        {/* HEADER */}
        <div className="of-header">
          <div className="of-eyebrow">Compression Sock Orders</div>
          <h1 className="of-title of-serif">Order Management</h1>
          <p className="of-subtitle">Create new orders or view and manage existing ones.</p>
        </div>

        {/* TABS */}
        <div className="of-tabs">
          <button
            className={`of-tab ${activeTab === "new" ? "active" : ""}`}
            onClick={() => setActiveTab("new")}
          >
            ✦ New Order
          </button>
          <button
            className={`of-tab ${activeTab === "history" ? "active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            ☰ Order History
          </button>
        </div>

        {/* NEW ORDER TAB */}
        {activeTab === "new" && (
          <>
            {/* PROGRESS */}
            <div className="of-progress">
              {STEPS.map((s, i) => (
                <div key={i} className="of-step-wrap">
                  <button className="of-step-btn">
                    <div className={`of-step-dot ${i < step ? "done" : i === step ? "active" : "inactive"}`}>
                      {i < step ? "✓" : i + 1}
                    </div>
                    <span className={`of-step-label ${i < step ? "done" : i === step ? "active" : "inactive"}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 && <div className={`of-step-line ${i < step ? "done" : ""}`} />}
                </div>
              ))}
            </div>

            {/* SECTION 1 — Patient & Provider */}
            {step === 0 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <div className="of-card">
                  <div className="of-card-header">
                    <div className="of-card-icon">👤</div>
                    <span className="of-card-title of-serif">Patient & Provider</span>
                  </div>
                  <div className="of-card-body">
                    <div className="of-grid">
                      <Field label="Patient Name (Last, First)" required>
                        <input className="of-input" type="text" value={form.patient_name_or_id} onChange={set("patient_name_or_id")} placeholder="e.g. Smith, John" />
                      </Field>
                      <Field label="Patient DOB">
                        <input className="of-input" type="date" value={form.patient_dob} onChange={set("patient_dob")} />
                      </Field>
                      <Field label="Phone #">
                        <input className="of-input" type="tel" value={form.phone_number} onChange={set("phone_number")} placeholder="607-595-8797" />
                      </Field>
                      <Field label="Email Address">
                        <input className="of-input" type="email" value={form.email_address} onChange={set("email_address")} placeholder="patient@example.com" />
                      </Field>
                      <Field label="Physician">
                        <input className="of-input" type="text" value={form.physician} onChange={set("physician")} placeholder="Dr. Last Name" />
                      </Field>
                      <Field label="Doctor Location">
                        <input className="of-input" type="text" value={form.doctor_location} onChange={set("doctor_location")} placeholder="Clinic or office location" />
                      </Field>
                    </div>
                  </div>
                </div>
                <div className="of-nav">
                  <div />
                  <button className="of-btn-next" onClick={() => setStep(1)}>
                    Next: Insurance & Clinical →
                  </button>
                </div>
              </motion.div>
            )}

            {/* SECTION 2 — Insurance & Clinical */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <div className="of-card">
                  <div className="of-card-header">
                    <div className="of-card-icon">🏥</div>
                    <span className="of-card-title of-serif">Insurance</span>
                  </div>
                  <div className="of-card-body">
                    <div className="of-grid">
                      <Field label="Primary Insurance">
                        <input className="of-input" type="text" value={form.primary_insurance} onChange={set("primary_insurance")} placeholder="Insurance provider" />
                      </Field>
                      <Field label="Primary Member ID">
                        <input className="of-input" type="text" value={form.primary_member_id} onChange={set("primary_member_id")} placeholder="Member ID" />
                      </Field>
                      <Field label="Ins #2">
                        <input className="of-input" type="text" value={form.ins2} onChange={set("ins2")} placeholder="Secondary insurance" />
                      </Field>
                      <Field label="Ins 2 ID">
                        <input className="of-input" type="text" value={form.ins2_id} onChange={set("ins2_id")} placeholder="Secondary member ID" />
                      </Field>
                    </div>
                  </div>
                </div>
                <div className="of-card">
                  <div className="of-card-header">
                    <div className="of-card-icon">🩺</div>
                    <span className="of-card-title of-serif">Clinical Details</span>
                  </div>
                  <div className="of-card-body">
                    <div className="of-grid">
                      <Field label="Dx Code 1">
                        <input className="of-input" type="text" value={form.icd10_code} onChange={set("icd10_code")} placeholder="e.g. I87.2" />
                      </Field>
                      <Field label="Dx Code 2">
                        <input className="of-input" type="text" value={form.dx_code_2} onChange={set("dx_code_2")} placeholder="e.g. I87.2" />
                      </Field>
                      <Field label="Ankle Measurement (in)">
                        <input className="of-input" type="text" value={form.ankle_measurement} onChange={set("ankle_measurement")} placeholder='e.g. 9.5"' />
                      </Field>
                      <Field label="Calf Measurement (in)">
                        <input className="of-input" type="text" value={form.calf_measurement} onChange={set("calf_measurement")} placeholder='e.g. 14"' />
                      </Field>
                      <Field label="Thigh Measurement (in)">
                        <input className="of-input" type="text" value={form.thigh_measurement} onChange={set("thigh_measurement")} placeholder='e.g. 20"' />
                      </Field>
                      <Field label="Length — Wraps Only">
                        <input className="of-input" type="text" value={form.length_wraps} onChange={set("length_wraps")} placeholder='e.g. 36"' />
                      </Field>
                    </div>
                  </div>
                </div>
                <div className="of-nav">
                  <button className="of-btn-back" onClick={() => setStep(0)}>← Back</button>
                  <button className="of-btn-next" onClick={() => setStep(2)}>
                    Next: Order Details →
                  </button>
                </div>
              </motion.div>
            )}

            {/* SECTION 3 — Order Details */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <div className="of-card">
                  <div className="of-card-header">
                    <div className="of-card-icon">📋</div>
                    <span className="of-card-title of-serif">Order Details</span>
                  </div>
                  <div className="of-card-body">
                    <div className="of-grid">
                      <Field label="Status">
                        <select className="of-select" value={form.compression_sock_status} onChange={set("compression_sock_status")}>
                          <option value="">Select status</option>
                          {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Medical Records">
                        <select className="of-select" value={form.medical_records_status} onChange={set("medical_records_status")}>
                          <option value="">Select</option>
                          {MEDICAL_RECORDS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Date Order Received">
                        <input className="of-input" type="date" value={form.date_order_received} onChange={set("date_order_received")} />
                      </Field>
                      <Field label="Date Sent to Billing">
                        <input className="of-input" type="date" value={form.date_sent_to_billing} onChange={set("date_sent_to_billing")} />
                      </Field>
                      <Field label="HCPC">
                        <input className="of-input" type="text" value={form.hcpc} onChange={set("hcpc")} placeholder="e.g. A6021" />
                      </Field>
                      <Field label="Product">
                        <input className="of-input" type="text" value={form.product_sku} onChange={set("product_sku")} placeholder="Product name" />
                      </Field>
                      <Field label="Size">
                        <select className="of-select" value={form.size} onChange={set("size")}>
                          <option value="">Select size</option>
                          {SIZE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                    </div>
                  </div>
                </div>
                <div className="of-nav">
                  <button className="of-btn-back" onClick={() => setStep(1)}>← Back</button>
                  <button className="of-btn-submit" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Order"}
                  </button>
                </div>
              </motion.div>
            )}
          </>
        )}

        {/* ORDER HISTORY TAB */}
        {activeTab === "history" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <OrderHistory />
          </motion.div>
        )}

      </div>
    </>
  );
};

export default OrderForm;